import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '@/lib/api';
import { AuthContextType } from '@/types';

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // 初始化时检查本地存储的 token
  useEffect(() => {
    const savedToken = localStorage.getItem('upload-manager-token');
    if (savedToken) {
      setToken(savedToken);
      // 验证 token 是否有效
      verifyToken(savedToken);
    } else {
      setLoading(false);
    }
  }, []);

  // 验证 token 有效性
  const verifyToken = async (_tokenToVerify: string) => {
    try {
      const result = await authApi.verify();
      if (result.success) {
        setIsAuthenticated(true);
      } else {
        // Token 无效，清除本地存储
        localStorage.removeItem('upload-manager-token');
        setToken(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      // Token 验证失败，清除本地存储
      localStorage.removeItem('upload-manager-token');
      setToken(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // 登录方法
  const login = async (passcode: string) => {
    setLoading(true);
    try {
      const result = await authApi.login({ passcode });
      if (result.success) {
        const { token: newToken } = result.data;
        setToken(newToken);
        setIsAuthenticated(true);
        localStorage.setItem('upload-manager-token', newToken);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      setIsAuthenticated(false);
      setToken(null);
      localStorage.removeItem('upload-manager-token');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // 登出方法
  const logout = () => {
    setIsAuthenticated(false);
    setToken(null);
    localStorage.removeItem('upload-manager-token');
  };

  const value: AuthContextType = {
    isAuthenticated,
    token,
    login,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// 自定义 hook 来使用认证上下文
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}